import type { EntityInstance, JsonValue, MapDocument, PropInstance } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";
import { markerPlace, propPlace, type IssueNames } from "./issue-entries.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { referenceTargetOf, type EntityTypeDefinition, type JsonSchema, type ReferenceTarget } from "./schema.ts";

/**
 * Typed references on a map (ADR-021's amendment of 2026-09-27, ADR-070's amendment "typed references"): a
 * field a registered type declares as a reference holds the id of a prop or a marker of the same map. This
 * module finds every such value, resolves it or says why it is broken, refuses a pick that does not fit, lists
 * who references a thing, raises the issues a broken reference stops the compile with and gives the ends a
 * link is drawn between. Pure: it reads the map and the registered types and keeps nothing.
 */

/**
 * A thing a reference can start from or end on: a prop or a marker, by layer and id, as Go to names them.
 */
export type ReferenceThing = { readonly kind: "prop"; readonly layerId: string; readonly propId: string } | { readonly kind: "marker"; readonly layerId: string; readonly entityId: string };

/**
 * The types a map's references are read against.
 */
export interface ReferenceTypes {
  readonly objectTypes: readonly ObjectTypeDefinition[];
  readonly entityTypes: readonly EntityTypeDefinition[];
}

/**
 * One reference value found on a map: the thing whose field holds it, the field's name and title, what the
 * field declares it names, the id it holds, the thing of that id on this map (null when there is none) and why
 * it is broken (a clause such as "which is not on this map"), null when it is sound. A row of a list of
 * references carries its index, and its title is the field's with the row's number ("Opens 2").
 */
export interface ReferenceLink {
  readonly owner: ReferenceThing;
  readonly field: string;
  readonly index?: number;
  readonly title: string;
  readonly target: ReferenceTarget;
  readonly id: string;
  readonly found: ReferenceThing | null;
  readonly problem: string | null;
}

/**
 * A field a pick fills: its title and what it names.
 */
export interface ReferenceField {
  readonly title: string;
  readonly target: ReferenceTarget;
}

/**
 * Where a sound link is drawn: from the centre of its referring thing's box to its target's, in world pixels.
 */
export interface LinkEnds {
  readonly link: ReferenceLink;
  readonly from: { readonly x: number; readonly y: number };
  readonly to: { readonly x: number; readonly y: number };
}

/**
 * The map's props and markers by id, with the thing each is.
 */
interface Things {
  readonly props: ReadonlyMap<string, { readonly ref: ReferenceThing; readonly prop: PropInstance; readonly layer: { readonly id: string; readonly name: string } }>;
  readonly markers: ReadonlyMap<string, { readonly ref: ReferenceThing; readonly entity: EntityInstance; readonly layer: { readonly id: string; readonly name: string } }>;
}

/**
 * Indexes a map's props and markers by id.
 *
 * @param map The map.
 * @returns The index.
 */
function thingsOf(map: MapDocument): Things {
  const props = new Map<string, { ref: ReferenceThing; prop: PropInstance; layer: { id: string; name: string } }>();
  const markers = new Map<string, { ref: ReferenceThing; entity: EntityInstance; layer: { id: string; name: string } }>();
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        props.set(prop.id, { ref: { kind: "prop", layerId: layer.id, propId: prop.id }, prop, layer });
      }
    } else if (layer.kind === "entity") {
      for (const entity of layer.entities) {
        markers.set(entity.id, { ref: { kind: "marker", layerId: layer.id, entityId: entity.id }, entity, layer });
      }
    }
  }
  return { props, markers };
}

/**
 * The reference fields a schema declares, in its order, a list of references marked `list`.
 *
 * @param schema The schema.
 * @returns Each field's name, title and target.
 */
export function referenceFieldsOf(schema: JsonSchema): readonly (ReferenceField & { readonly name: string; readonly list?: true })[] {
  const fields: (ReferenceField & { name: string; list?: true })[] = [];
  for (const [name, property] of Object.entries(schema.properties)) {
    const target = referenceTargetOf(property);
    if (target !== undefined) {
      fields.push({ name, title: property.title ?? name, target, ...(property.type === "list" ? { list: true as const } : {}) });
    }
  }
  return fields;
}

/**
 * Whether any registered type declares a reference field, which is when the Links overlay is offered.
 *
 * @param types The registered types.
 * @returns True when one does.
 */
export function declaresReferences(types: ReferenceTypes): boolean {
  return [...types.objectTypes, ...types.entityTypes].some((type) => referenceFieldsOf(type.schema).length > 0);
}

/**
 * A type's label, or its id when the game does not register it.
 *
 * @param types The registered types of that kind.
 * @param type The type id.
 * @returns The label.
 */
function labelOf(types: readonly { readonly type: string; readonly label: string }[], type: string): string {
  return types.find((t) => t.type === type)?.label ?? type;
}

/**
 * Why an id does not name what a field declares, as a clause after the id, or null when it does.
 *
 * @param things The map's props and markers.
 * @param types The registered types.
 * @param target What the field names.
 * @param id The id the field holds.
 * @returns The thing of that id, if any, and the clause.
 */
function resolve(things: Things, types: ReferenceTypes, target: ReferenceTarget, id: string): { readonly found: ReferenceThing | null; readonly problem: string | null } {
  const prop = things.props.get(id);
  const marker = things.markers.get(id);
  if (target.kind === "prop") {
    if (prop !== undefined) {
      return { found: prop.ref, problem: prop.prop.behaviour?.type === target.type ? null : `which no longer carries ${labelOf(types.objectTypes, target.type)}` };
    }
    return { found: marker?.ref ?? null, problem: marker === undefined ? "which is not on this map" : "which is a marker, not a prop" };
  }
  if (marker !== undefined) {
    return { found: marker.ref, problem: marker.entity.type === target.type ? null : `which is no longer a ${labelOf(types.entityTypes, target.type)}` };
  }
  return { found: prop?.ref ?? null, problem: prop === undefined ? "which is not on this map" : "which is a prop, not a marker" };
}

/**
 * The reference values one thing's fields hold, each resolved.
 *
 * @param owner The thing.
 * @param schema Its type's schema.
 * @param fields Its field values.
 * @param things The map's props and markers.
 * @param types The registered types.
 * @returns Its links, in the schema's order and a list's rows in theirs; an empty or non-string value is none.
 */
function linksOf(owner: ReferenceThing, schema: JsonSchema, fields: { readonly [key: string]: JsonValue }, things: Things, types: ReferenceTypes): ReferenceLink[] {
  const links: ReferenceLink[] = [];
  for (const field of referenceFieldsOf(schema)) {
    const value = fields[field.name];
    const rows: readonly (readonly [JsonValue, number | undefined])[] = field.list === true ? (Array.isArray(value) ? value.map((id, index) => [id as JsonValue, index] as const) : []) : [[value ?? null, undefined]];
    for (const [id, index] of rows) {
      if (typeof id !== "string" || id === "") {
        continue;
      }
      const title = index === undefined ? field.title : `${field.title} ${index + 1}`;
      links.push({ owner, field: field.name, ...(index === undefined ? {} : { index }), title, target: field.target, id, ...resolve(things, types, field.target, id) });
    }
  }
  return links;
}

/**
 * Every reference value on a map, in document order: each behaviour prop's then each marker's, layer by layer,
 * each resolved or broken with its reason. A thing whose type the game does not register holds none, since only
 * a registered schema says which fields are references.
 *
 * @param map The map.
 * @param types The registered types.
 * @returns The links.
 */
export function mapReferences(map: MapDocument, types: ReferenceTypes): readonly ReferenceLink[] {
  const things = thingsOf(map);
  const links: ReferenceLink[] = [];
  for (const layer of map.layers) {
    if (layer.kind === "prop") {
      for (const prop of layer.props) {
        const behaviour = prop.behaviour;
        const type = behaviour === undefined ? undefined : types.objectTypes.find((t) => t.type === behaviour.type);
        if (behaviour !== undefined && type !== undefined) {
          links.push(...linksOf({ kind: "prop", layerId: layer.id, propId: prop.id }, type.schema, behaviour.fields, things, types));
        }
      }
    } else if (layer.kind === "entity") {
      for (const entity of layer.entities) {
        const type = types.entityTypes.find((t) => t.type === entity.type);
        if (type !== undefined) {
          links.push(...linksOf({ kind: "marker", layerId: layer.id, entityId: entity.id }, type.schema, entity.fields, things, types));
        }
      }
    }
  }
  return links;
}

/**
 * The id of a thing.
 *
 * @param thing The thing.
 * @returns Its id.
 */
export function thingId(thing: ReferenceThing): string {
  return thing.kind === "prop" ? thing.propId : thing.entityId;
}

/**
 * The sound links ending on any of the given ids from a thing outside them: who a delete of those ids would
 * leave with a broken reference.
 *
 * @param links The map's links.
 * @param ids The ids.
 * @returns The links, in document order.
 */
export function referrersOf(links: readonly ReferenceLink[], ids: Iterable<string>): readonly ReferenceLink[] {
  const set = new Set(ids);
  return links.filter((link) => link.problem === null && set.has(link.id) && !set.has(thingId(link.owner)));
}

/**
 * Why a picked thing cannot fill a field, or null when it fits: it must be a prop carrying the declared object
 * type, or a marker of the declared entity type, still on the map and not the field's own thing.
 *
 * @param map The map.
 * @param types The registered types.
 * @param owner The thing whose field is being filled.
 * @param field The field.
 * @param picked The thing picked.
 * @returns The status bar's reason, or null.
 */
export function pickRefusal(map: MapDocument, types: ReferenceTypes, owner: ReferenceThing, field: ReferenceField, picked: ReferenceThing): string | null {
  const { target, title } = field;
  const wanted = target.kind === "prop" ? `${title} takes a prop with ${labelOf(types.objectTypes, target.type)}` : `${title} takes a ${labelOf(types.entityTypes, target.type)} marker`;
  if (picked.kind === owner.kind && thingId(picked) === thingId(owner)) {
    return `${title} cannot name the ${owner.kind} it belongs to`;
  }
  const things = thingsOf(map);
  if (picked.kind !== target.kind) {
    return `${wanted}; this is a ${picked.kind}`;
  }
  if (picked.kind === "prop") {
    const behaviour = things.props.get(picked.propId)?.prop.behaviour;
    if (!things.props.has(picked.propId)) {
      return `${wanted}; that is no longer on the map`;
    }
    if (behaviour === undefined) {
      return `${wanted}; this prop has no behaviour`;
    }
    return behaviour.type === target.type ? null : `${wanted}; this prop carries ${labelOf(types.objectTypes, behaviour.type)}`;
  }
  const entity = things.markers.get(picked.entityId)?.entity;
  if (entity === undefined) {
    return `${wanted}; that is no longer on the map`;
  }
  return entity.type === target.type ? null : `${wanted}; this marker is a ${labelOf(types.entityTypes, entity.type)}`;
}

/**
 * The issues broken references raise (ADR-070's amendment "typed references"): one refusal on the referring
 * thing per broken value, naming the thing's type, the field, the id and why, with the fix after the
 * semicolon. The compile gate refuses a world holding any.
 *
 * @param map The map.
 * @param types The registered types.
 * @param links The map's links, when already read.
 * @returns The issues, in document order.
 */
export function referenceIssues(map: MapDocument, types: ReferenceTypes, links: readonly ReferenceLink[] = mapReferences(map, types)): readonly ValidationIssue[] {
  const things = thingsOf(map);
  const issues: ValidationIssue[] = [];
  for (const link of links) {
    if (link.problem === null) {
      continue;
    }
    const tail = `whose ${link.title} names ${link.id}, ${link.problem}; choose another or clear it`;
    if (link.owner.kind === "prop") {
      const type = things.props.get(link.owner.propId)?.prop.behaviour?.type ?? "";
      issues.push({ mapId: map.id, kind: "reference.broken", layerId: link.owner.layerId, propId: link.owner.propId, message: `prop ${link.owner.propId} is a ${labelOf(types.objectTypes, type)} ${tail}` });
    } else {
      const type = things.markers.get(link.owner.entityId)?.entity.type ?? "";
      issues.push({ mapId: map.id, kind: "reference.broken", layerId: link.owner.layerId, entityId: link.owner.entityId, message: `marker ${link.owner.entityId} is a ${labelOf(types.entityTypes, type)} ${tail}` });
    }
  }
  return issues;
}

/**
 * Where a thing stands, named as the Issues list names it, or null when it is not on the map.
 *
 * @param map The map.
 * @param thing The thing.
 * @param names The lookups.
 * @returns The place.
 */
export function thingPlace(map: MapDocument, thing: ReferenceThing, names: IssueNames): ReturnType<typeof propPlace> | ReturnType<typeof markerPlace> | null {
  const layer = map.layers.find((l) => l.id === thing.layerId);
  if (thing.kind === "prop" && layer?.kind === "prop") {
    const prop = layer.props.find((p) => p.id === thing.propId);
    return prop === undefined ? null : propPlace(prop, layer, names);
  }
  if (thing.kind === "marker" && layer?.kind === "entity") {
    const entity = layer.entities.find((e) => e.id === thing.entityId);
    return entity === undefined ? null : markerPlace(entity, layer, names);
  }
  return null;
}

/**
 * The ends of every sound link: the centre of the box its referring thing covers (a prop's art, a marker's
 * tile) and its target's. A broken link has no end to draw.
 *
 * @param map The map.
 * @param links The map's links.
 * @param names The lookups the boxes are measured with.
 * @returns The ends, in the links' order.
 */
export function linkEnds(map: MapDocument, links: readonly ReferenceLink[], names: IssueNames): readonly LinkEnds[] {
  const ends: LinkEnds[] = [];
  for (const link of links) {
    if (link.problem !== null || link.found === null) {
      continue;
    }
    const from = thingPlace(map, link.owner, names);
    const to = thingPlace(map, link.found, names);
    if (from !== null && to !== null) {
      ends.push({ link, from: { x: from.box.x + from.box.w / 2, y: from.box.y + from.box.h / 2 }, to: { x: to.box.x + to.box.w / 2, y: to.box.y + to.box.h / 2 } });
    }
  }
  return ends;
}

/**
 * Which links the map draws (ADR-070's amendment "typed references"): every link one of whose ends is
 * selected, and with the Links overlay on every link whose two ends are on shown layers.
 *
 * @param ends The ends of every sound link.
 * @param view Whether the overlay is on, the selected things and which layers are hidden.
 * @returns The links to draw, in their order.
 */
export function shownLinks(ends: readonly LinkEnds[], view: { readonly overlay: boolean; readonly selected: readonly ReferenceThing[]; readonly hidden: (layerId: string) => boolean }): readonly LinkEnds[] {
  const key = (thing: ReferenceThing): string => `${thing.kind}\n${thing.layerId}\n${thingId(thing)}`;
  const selected = new Set(view.selected.map(key));
  return ends.filter(({ link }) => {
    const found = link.found as ReferenceThing;
    if (selected.has(key(link.owner)) || selected.has(key(found))) {
      return true;
    }
    return view.overlay && !view.hidden(link.owner.layerId) && !view.hidden(found.layerId);
  });
}
